/**
 * The branches this browser tab has just deleted, so that « Suppression en
 * cours » shows at once — before ArgoCD or GitLab is read again (signal (c)
 * of `branchDeletion.ts`).
 *
 * In `sessionStorage`: it survives the navigation to the project page that
 * follows a deletion and a reload, and stays with the tab that deleted —
 * another tab learns of it from ArgoCD and GitLab like everyone else.
 *
 * An entry is written once GitLab has deleted the branch (`deleteBranch`,
 * branchManagement.ts) and goes away:
 *  - when a fresh read of the project's Applications no longer lists the
 *    branch (`prunePendingDeletions`, from the environments card and the
 *    branch page, after their FORCED reads only — Refresh, polls — never
 *    after the list they share for 30 s, `projectApplications.ts`);
 *  - when a branch of the same name is created again (`createBranch`);
 *  - when a complete git list asked for after the deletion still holds the
 *    branch — pushed again from elsewhere (`dropPendingDeletionsSeenInGit`,
 *    from every screen reading the git list, `branchDeletion.ts`). Safe with
 *    the backend's 30 s cache of that list only under ONE backend process:
 *    see « ONE BACKEND PROCESS ASSUMED » in
 *    `plugins/waadoo-core-backend/src/plugins/waadooProjects/branchRoutes.ts`;
 *  - after {@link PENDING_DELETION_TTL_MS} in any case.
 *
 * Storage is guarded like the sidebar's (`ProjectsNav.tsx`): a browser
 * with storage disabled throws on access, and this is a convenience — it then
 * holds nothing, and the two other signals remain.
 */

import { useMemo, useSyncExternalStore } from 'react';

/** The `sessionStorage` key. */
export const PENDING_DELETIONS_KEY = '@waadoo/projects/pending-deletions';

/**
 * How long a deletion is assumed in progress without anything else saying
 * so. The ApplicationSet notices the branch is gone within 180 s
 * (`requeueAfterSeconds`), and the finalizer's cleanup takes a few more
 * minutes; past ten, ArgoCD's own `deletionTimestamp` or the git list take
 * over, or the environment is gone.
 */
export const PENDING_DELETION_TTL_MS = 10 * 60_000;

export interface PendingDeletion {
  project: string;
  branch: string;
  /** Epoch milliseconds of the deletion. */
  at: number;
}

const listeners = new Set<() => void>();

function readRaw(): string | null {
  try {
    return window.sessionStorage.getItem(PENDING_DELETIONS_KEY);
  } catch {
    return null;
  }
}

/** The stored entries, the malformed ones dropped. No TTL applied here. */
export function parsePendingDeletions(raw: string | null): PendingDeletion[] {
  if (!raw) {
    return [];
  }
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((item: unknown) => {
    const entry = (item ?? {}) as Partial<
      Record<keyof PendingDeletion, unknown>
    >;
    return typeof entry.project === 'string' &&
      typeof entry.branch === 'string' &&
      typeof entry.at === 'number' &&
      Number.isFinite(entry.at)
      ? [{ project: entry.project, branch: entry.branch, at: entry.at }]
      : [];
  });
}

/** Whether `entry` is still within its TTL at `now`. */
function isLive(entry: PendingDeletion, now: number): boolean {
  return now - entry.at < PENDING_DELETION_TTL_MS;
}

function write(entries: PendingDeletion[]): void {
  try {
    if (entries.length === 0) {
      window.sessionStorage.removeItem(PENDING_DELETIONS_KEY);
    } else {
      window.sessionStorage.setItem(
        PENDING_DELETIONS_KEY,
        JSON.stringify(entries),
      );
    }
  } catch {
    // Storage disabled or full: the two other signals remain.
  }
  // `storage` events reach the OTHER tabs only; this one is told here.
  listeners.forEach(listener => listener());
}

/** Records the deletion of `branch`; the expired entries go meanwhile. */
export function markPendingDeletion(
  project: string,
  branch: string,
  now: number = Date.now(),
): void {
  write([
    ...parsePendingDeletions(readRaw()).filter(
      entry =>
        isLive(entry, now) &&
        !(entry.project === project && entry.branch === branch),
    ),
    { project, branch, at: now },
  ]);
}

/** Forgets the deletion of `branch`, if one is recorded. */
export function clearPendingDeletion(project: string, branch: string): void {
  const entries = parsePendingDeletions(readRaw());
  const kept = entries.filter(
    entry => !(entry.project === project && entry.branch === branch),
  );
  if (kept.length !== entries.length) {
    write(kept);
  }
}

/**
 * Forgets the deletions of `project` whose branch `present` no longer lists.
 * `present` must come from a read made NOW, never from a cache: a list older
 * than the deletion may lack a branch created just before it.
 */
export function prunePendingDeletions(
  project: string,
  present: Iterable<string>,
): void {
  const branches = new Set(present);
  const entries = parsePendingDeletions(readRaw());
  const kept = entries.filter(
    entry => entry.project !== project || branches.has(entry.branch),
  );
  if (kept.length !== entries.length) {
    write(kept);
  }
}

/**
 * Forgets the deletions of `project` whose branch is in `branches`, a complete
 * git list asked for at `requestedAt` (browser clock, like the marks) — when
 * the request started after the mark. The request time and not the answer's:
 * a request sent just before the deletion may answer after the mark, still
 * holding the branch, and must not clear it.
 */
export function dropPendingDeletionsSeenInGit(
  project: string,
  branches: ReadonlySet<string>,
  requestedAt: number,
): void {
  const entries = parsePendingDeletions(readRaw());
  const kept = entries.filter(
    entry =>
      entry.project !== project ||
      !branches.has(entry.branch) ||
      entry.at >= requestedAt,
  );
  if (kept.length !== entries.length) {
    write(kept);
  }
}

/** Whether `entries` hold a live deletion of `branch` of `project`. */
export function isPendingDeletion(
  entries: readonly PendingDeletion[],
  project: string,
  branch: string,
  now: number,
): boolean {
  return entries.some(
    entry =>
      entry.project === project &&
      entry.branch === branch &&
      isLive(entry, now),
  );
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Another tab of the same session cannot write this tab's sessionStorage,
  // so no `storage` listener: this tab's writes are the only changes.
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The recorded deletions, re-rendering on every change made in this tab.
 * The snapshot is the stored string, compared by value, so the array keeps
 * its identity between changes and can be a dependency.
 */
export function usePendingDeletions(): readonly PendingDeletion[] {
  const raw = useSyncExternalStore(subscribe, readRaw, () => null);
  return useMemo(() => parsePendingDeletions(raw), [raw]);
}
